import { z } from 'zod';
import { nullablePositiveIdSchema, positiveIdSchema } from '../../shared/index.js';
export const MinutesChatPreset = {
    recommendMotions: 'recommend_motions',
    summarizeNotes: 'summarize_notes',
};
export const MinutesChatTranscriptScope = {
    entryWindow: 'entry_window',
    fullMeeting: 'full_meeting',
};
export const MinutesChatMotionKind = {
    main: 'main',
    privileged: 'privileged',
    subsidiary: 'subsidiary',
};
export const minutesChatMessageSchema = z.object({
    role: z.enum(['assistant', 'user']),
    content: z.string().min(1),
});
export const minutesChatPresetSchema = z.enum([
    MinutesChatPreset.summarizeNotes,
    MinutesChatPreset.recommendMotions,
]);
export const minutesChatTranscriptScopeSchema = z.enum([
    MinutesChatTranscriptScope.entryWindow,
    MinutesChatTranscriptScope.fullMeeting,
]);
export const minutesChatRequestSchema = z.object({
    messages: z.array(minutesChatMessageSchema).min(1),
    minutesEntryId: nullablePositiveIdSchema.optional(),
    preset: minutesChatPresetSchema.optional(),
    transcriptScope: minutesChatTranscriptScopeSchema.optional(),
});
export const minutesChatTimestampSecondsSchema = z.number().nonnegative().nullable();
export const minutesChatMotionKindSchema = z.enum([
    MinutesChatMotionKind.main,
    MinutesChatMotionKind.subsidiary,
    MinutesChatMotionKind.privileged,
]);
const minutesChatVoteSuggestionSchema = z.object({
    meetingMemberId: positiveIdSchema,
    vote: z.enum(['absent', 'abstain', 'aye', 'nay', 'recuse']),
});
export const minutesChatNoteSuggestionSchema = z.object({
    content: z.string().min(1),
    isPrivate: z.boolean().default(false),
    noteId: positiveIdSchema.optional(),
    suggestedMinutesEntryId: positiveIdSchema.optional(),
    timestampSeconds: minutesChatTimestampSecondsSchema.optional(),
});
const minutesChatMotionSuggestionSharedSchema = z.object({
    includeVotes: z.boolean().default(true),
    motionId: positiveIdSchema.optional(),
    motionKind: minutesChatMotionKindSchema,
    motionText: z.string().nullable().optional(),
    moverId: nullablePositiveIdSchema.optional(),
    parentMotionId: nullablePositiveIdSchema.optional(),
    seconderId: nullablePositiveIdSchema.optional(),
    suggestedMinutesEntryId: positiveIdSchema.optional(),
    timestampSeconds: minutesChatTimestampSecondsSchema.optional(),
    votes: z.array(minutesChatVoteSuggestionSchema).optional(),
});
const requireLegislativeActionIdentifier = (value, ctx) => {
    if (value.legislativeActionId != null || value.legislativeActionCode?.trim()) {
        return;
    }
    ctx.addIssue({
        code: 'custom',
        message: 'Either legislativeActionId or legislativeActionCode is required',
        path: ['legislativeActionId'],
    });
};
/**
 * Raw OpenAI motion suggestion shape before server-side normalization resolves legislative actions.
 */
export const minutesChatModelMotionSuggestionSchema = minutesChatMotionSuggestionSharedSchema
    .extend({
    legislativeActionCode: z.string().optional(),
    legislativeActionId: positiveIdSchema.optional(),
})
    .superRefine(requireLegislativeActionIdentifier);
/** Normalized motion suggestion returned to clients after legislative action resolution. */
export const minutesChatMotionSuggestionSchema = minutesChatMotionSuggestionSharedSchema.extend({
    legislativeActionCode: z.string(),
    legislativeActionId: positiveIdSchema,
});
export const minutesChatSuggestionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('note'),
        suggestion: minutesChatNoteSuggestionSchema,
    }),
    z.object({
        type: z.literal('motion'),
        suggestion: minutesChatMotionSuggestionSchema,
    }),
]);
export const minutesChatContextSchema = z.object({
    mediaArchiveId: positiveIdSchema,
    segmentCount: z.number().int().nonnegative(),
    transcriptScope: z.enum([
        MinutesChatTranscriptScope.entryWindow,
        MinutesChatTranscriptScope.fullMeeting,
    ]),
    truncated: z.boolean(),
    windowEndSeconds: z.number().nonnegative().optional(),
    windowStartSeconds: z.number().nonnegative().optional(),
});
export const minutesChatAvailabilitySchema = z.object({
    fullMeetingCharacterCount: z.number().int().nonnegative(),
    fullMeetingContextAvailable: z.boolean(),
    fullMeetingMaxCharacters: z.number().int().positive(),
    mediaArchiveId: positiveIdSchema.optional(),
    transcriptDocumentId: positiveIdSchema.optional(),
});
export const minutesChatResponseSchema = z.object({
    assistantMessage: z.string(),
    context: minutesChatContextSchema,
    suggestions: z.array(minutesChatSuggestionSchema).optional(),
});
/**
 * Strips common assistant-message prefaces before using prose as motion text.
 */
export const stripMinutesChatMotionAssistantPreamble = (message) => {
    const trimmed = message.trim();
    if (!trimmed) {
        return trimmed;
    }
    const withoutPrefix = trimmed.replace(/^Motion suggestions?\s*(?:\([^)]+\))?\s*(?:for\s+[^:]+?)?:\s*/i, '');
    return withoutPrefix.trim() || trimmed;
};
/**
 * Resolves motion text from a suggestion, falling back to assistant prose when needed.
 */
export const resolveMinutesChatMotionText = (motionText, assistantMessageFallback) => {
    const trimmedMotionText = motionText?.trim();
    if (trimmedMotionText) {
        return trimmedMotionText;
    }
    if (!assistantMessageFallback?.trim()) {
        return null;
    }
    return stripMinutesChatMotionAssistantPreamble(assistantMessageFallback);
};
